import { GroupedListingSkeleton } from './grouped-listing-skeleton';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { Event, track } from 'app/lib/analytics';
import { useManyListingsDistances, useOneListing } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import MemberRow from 'app/screens/shared/grouped-listing/components/member-row';
import { useOnFocus } from 'app/services/on-focus';
import type { Listing } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useIsGuestWeb, usePageTitle } from 'app/utils/web-helpers';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import React, { useEffect } from 'react';
import { GroupedListingSummary } from './components/concierge-card';
import { HeaderSection } from './components/header-section';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const GroupedListingScreenComponent: NavigationScreen<'grouped-listing'> = ({ route }) => {
  const { t } = useScopedTranslation();
  const isGuestWeb = useIsGuestWeb();
  const { data: user } = useUser();
  const conciergeCtaEnabled =
    !isGuestWeb &&
    Boolean(user && isFeatureEnabled(DynamicFeatureFlag.GroupedListingConciergerieCta, user.featureFlags));

  const updatedListingQuery = useOneListing(
    route.params.id,
    'grouped-listing',
    {},
    {
      searchCenterLatitude: route.params.searchCenterLatitude,
      searchCenterLongitude: route.params.searchCenterLongitude,
      searchDistance: route.params.searchDistance,
    },
  );
  const listing = updatedListingQuery.data;

  useOnFocus(() => {
    if (listing) {
      void updatedListingQuery.refetch();
    }
  });

  const groupingKey = listing?.grouping?.key;
  const [, groupingName] = groupingKey?.split('|') ?? [];

  const listingDistanceQuery = useManyListingsDistances(listing?.grouping?.listings.map((l) => l.id) ?? [], {
    enabled: listing?.grouping?.listings && listing.grouping.listings.length > 0,
  });

  const getDistanceResultFromListing = (listingId: Listing['id']) => {
    if (!Array.isArray(listingDistanceQuery.data)) {
      return undefined;
    }
    return listingDistanceQuery.data.find((l) => l.id === listingId);
  };

  const listings = (() => {
    return (
      listing?.grouping?.listings
        .map((l, index) => {
          const listingStats = getDistanceResultFromListing(l.id);
          return {
            ...l,
            originalIndex: index,
            distance: listingStats?.distance ?? l.distance ?? undefined,
            city: listingStats?.city,
            user: {
              ...l.user,
              trustPoints: listingStats?.trustPoints,
            },
          };
        })
        .sort((l1, l2) => {
          const distanceDelta = (l1.distance ?? MAX_RADIUS_IN_METERS) - (l2.distance ?? MAX_RADIUS_IN_METERS);
          if (distanceDelta !== 0) {
            return distanceDelta;
          }
          return l1.originalIndex - l2.originalIndex;
        }) ?? []
    );
  })();

  const setPageTitle = usePageTitle();

  useEffect(
    () =>
      setPageTitle(`${listings.length.toFixed(0)} x ${t('title.offer', { ns: 'one-listing' })}s - {{name}}`, {
        name: listing?.item.name,
      }),
    [listing?.item.name, listing?.type, listings.length, setPageTitle, t],
  );

  useEffect(() => {
    if (listing) {
      track(Event.ListingsGroupingOpened, {
        grouping_name: groupingName!,
        listing_type: listing.type,
        listing_category: listing.categoryPrimary.id,
      });
    }
  }, [groupingName, listing]);

  useRegisterLoading(updatedListingQuery.isPending, 'GroupedListingScreen');
  useRegisterLoading(!isGuestWeb && listingDistanceQuery.isPending, 'GroupedListingScreen-distances');

  if (updatedListingQuery.isPending || (!isGuestWeb && listingDistanceQuery.data === undefined)) {
    return <GroupedListingSkeleton />;
  }

  return (
    <Page>
      <Column gap="spacing.general.x8">
        {listing ? (
          conciergeCtaEnabled ? (
            <GroupedListingSummary listing={listing} isGuest={isGuestWeb} />
          ) : (
            <HeaderSection {...listing} />
          )
        ) : null}

        <Column gap="spacing.general.x6">
          <Text fontWeight="bold" variant="bodyLarge" accessibilityRole="header">
            {t(conciergeCtaEnabled ? 'listHeader' : 'listHeaderLegacy')}
          </Text>

          <Column style={{ borderTopWidth: 1, borderTopColor: theme.colors['border.secondary'] }}>
            {listings.map((item) => (
              <MemberRow key={item.id} {...item} />
            ))}
          </Column>
        </Column>
      </Column>
    </Page>
  );
};

export const GroupedListingScreen: NavigationScreen<'grouped-listing'> = withOptionalMetrics(
  'GroupedListingScreen',
  withErrorBoundary(withDashboardLayout(GroupedListingScreenComponent)),
);
